import { motion } from "framer-motion";
import { Zap, Link2, Brain, Shield } from "lucide-react";
import { IMAGES } from "@/lib/images";

const TECH_FEATURES = [
  {
    icon: Brain,
    title: "Antigravity",
    subtitle: "次世代AI開発環境",
    description: "Googleが提供するAIネイティブな開発環境。自然言語の指示だけで、業務に最適化されたAIエージェントを迅速に構築できます。",
  },
  {
    icon: Link2,
    title: "Workspace MCP",
    subtitle: "Model Context Protocol",
    description: "AIエージェントがGmail、カレンダー、ドライブ、スプレッドシートなどのWorkspaceデータにセキュアにアクセスし、業務を自動実行するための標準プロトコルです。",
  },
  {
    icon: Zap,
    title: "Gemini AI",
    subtitle: "マルチモーダルAI",
    description: "テキスト・画像・音声を横断的に理解するGoogleの最新AI。複雑な業務判断や文書処理を、人間に近い精度で自動化します。",
  },
  {
    icon: Shield,
    title: "エンタープライズセキュリティ",
    subtitle: "Google Cloud基盤",
    description: "Google Cloudのセキュリティ基盤上で動作。データは暗号化され、アクセス制御も細かく設定可能。中小企業でも安心して利用できます。",
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export function TechnologySection() {
  return (
    <section id="technology" className="py-14 sm:py-20 lg:py-28 bg-white">
      <div className="container">
        {/* Section Header */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-16">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Technology
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            Googleの最新技術で、業務を根本から変える
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-2xl leading-relaxed">
            Antigravity × Workspace MCP × Gemini AI。3つの技術を組み合わせることで、Google Workspaceの業務データを起点にした高度な自動化を実現します。
          </p>
        </motion.div>

        {/* Two-column layout: Image + Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-start">
          {/* Left: Illustration */}
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-[#E0E0E0]">
              <img
                src={IMAGES.serviceAutomation}
                alt="AI業務自動化の概念図"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating stat badge */}
            <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 lg:-bottom-6 lg:-right-6 bg-white border border-[#E0E0E0] shadow-lg p-3 sm:p-4 lg:p-5">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#E03030]" style={{ fontFamily: "'Inter', sans-serif" }}>
                60%
              </div>
              <div className="text-[10px] sm:text-xs text-[#333333] mt-1">定型業務の削減率</div>
            </div>
          </motion.div>

          {/* Right: Feature list */}
          <div className="space-y-3 sm:space-y-6">
            {TECH_FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                className="group flex gap-3 sm:gap-5 p-3 sm:p-5 border border-transparent hover:border-[#E0E0E0] hover:bg-[#F9F9F9] transition-all duration-300 rounded-sm"
              >
                <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-[#1A1A1A] text-white rounded-sm">
                  <feature.icon size={18} strokeWidth={1.5} className="sm:hidden" />
                  <feature.icon size={20} strokeWidth={1.5} className="hidden sm:block" />
                </div>
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 mb-1">
                    <h3 className="text-sm sm:text-base font-bold text-[#1A1A1A]">{feature.title}</h3>
                    <span className="text-[10px] sm:text-xs text-[#666666]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {feature.subtitle}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-[#333333] leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
